import React, { useRef, useState } from 'react';
import {
    AiBrain01Icon,
    File01Icon,
    Folder01Icon,
    FolderLibraryIcon,
    Layers01Icon,
    Message01Icon,
    Pdf01Icon,
    Doc01Icon,
    SourceCodeIcon,
    Target02Icon,
    Xls01Icon,
} from 'hugeicons-react';
import FeatureCards, { FeatureCardItem } from './FeatureCards';
import { TRIAL_DAYS } from '../../Data/PricingData';
import '../../Styles/Pricing/StudySpacesShowcase.css';

type FileKind = 'pdf' | 'doc' | 'xls' | 'code' | 'text';

interface DemoSpace {
    id: string;
    title: string;
    instructions: string;
    capacity: number;
    files: { name: string; kind: FileKind; size: string }[];
    chats: { title: string; when: string }[];
    recap: { question: string; answer: string };
}

const DEMO_SPACES: DemoSpace[] = [
    {
        id: 'launch',
        title: 'Q3 Product Launch',
        instructions: 'Help me plan the Q3 launch: draft messaging, keep track of open decisions, and turn meeting notes into action items.',
        capacity: 46,
        files: [
            { name: 'Launch Brief.pdf', kind: 'pdf', size: '2.4 MB' },
            { name: 'Pricing Research.xlsx', kind: 'xls', size: '380 KB' },
            { name: 'Kickoff Notes.docx', kind: 'doc', size: '96 KB' },
        ],
        chats: [
            { title: 'Messaging Drafts', when: '2h ago' },
            { title: 'Meeting Notes to Tasks', when: 'Yesterday' },
            { title: 'Competitor Comparison', when: '3 days ago' },
        ],
        recap: {
            question: 'Where did we land on the launch plan?',
            answer: 'You locked in September 30 as the launch date and chose an annual discount for early customers. Still open: final pricing copy and the partner email, both due Friday.',
        },
    },
    {
        id: 'budget-app',
        title: 'Budget App Side Project',
        instructions: "I'm building a budgeting app in Python. Review my code, suggest a clean structure, and keep useful snippets and decisions together.",
        capacity: 18,
        files: [
            { name: 'budget_model.py', kind: 'code', size: '12 KB' },
            { name: 'API Notes.md', kind: 'text', size: '8 KB' },
            { name: 'Database Schema.pdf', kind: 'pdf', size: '640 KB' },
        ],
        chats: [
            { title: 'API Design', when: 'Today' },
            { title: 'Bug Hunting', when: 'Yesterday' },
        ],
        recap: {
            question: 'What should I work on next?',
            answer: 'Last time you moved categories into their own table. Next up: the monthly rollup query, then tests for recurring expenses.',
        },
    },
    {
        id: 'kitchen',
        title: 'Kitchen Renovation',
        instructions: 'Help me compare contractor quotes, keep the budget on track, and research materials and layouts.',
        capacity: 63,
        files: [
            { name: 'Contractor Quotes.pdf', kind: 'pdf', size: '1.1 MB' },
            { name: 'Budget.xlsx', kind: 'xls', size: '54 KB' },
            { name: 'Floor Plan.pdf', kind: 'pdf', size: '3.2 MB' },
        ],
        chats: [
            { title: 'Quote Comparison', when: 'Today' },
            { title: 'Countertop Options', when: '4 days ago' },
        ],
        recap: {
            question: 'Are we still under budget?',
            answer: "Yes, by about $1,800 if you go with the second contractor. You were still choosing between quartz and butcher block; quartz would use most of that margin.",
        },
    },
];

const FILE_ICON: Record<FileKind, React.ReactNode> = {
    pdf: <Pdf01Icon size={18} />,
    doc: <Doc01Icon size={18} />,
    xls: <Xls01Icon size={18} />,
    code: <SourceCodeIcon size={18} />,
    text: <File01Icon size={18} />,
};

const FEATURES: FeatureCardItem[] = [
    {
        icon: <Target02Icon />,
        title: 'Instructions for Every Chat',
        text: 'Set your goals once and every chat in the Space follows them.',
    },
    {
        icon: <FolderLibraryIcon />,
        title: 'Shared Materials',
        text: 'Upload PDFs, Word, PowerPoint, Excel, text and code files for Buddy to draw on.',
    },
    {
        icon: <AiBrain01Icon />,
        title: 'Remembers Your Progress',
        text: 'Decisions, next steps and deadlines carry over from one chat to the next.',
    },
    {
        icon: <Layers01Icon />,
        title: 'One Place per Project',
        text: 'Keep every chat and file for a project side by side instead of lost in your history.',
    },
];

interface SpaceShowcaseProps {
    scrollToPricing: () => void;
    isExternal?: boolean;
}

const SpaceShowcase: React.FC<SpaceShowcaseProps> = ({ scrollToPricing, isExternal = false }) => {
    const [activeId, setActiveId] = useState(DEMO_SPACES[0].id);
    const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
    const space = DEMO_SPACES.find(s => s.id === activeId) ?? DEMO_SPACES[0];

    // On narrow screens the tabs scroll sideways; keep the selected one visible.
    const select = (id: string, focus = false) => {
        setActiveId(id);
        const tab = tabRefs.current[id];
        if (focus) tab?.focus();
        tab?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    };

    const onTabKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        const delta = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key];
        if (!delta) return;
        e.preventDefault();
        select(DEMO_SPACES[(index + delta + DEMO_SPACES.length) % DEMO_SPACES.length].id, true);
    };

    return (
        <section id="study-spaces" className="study-spaces-showcase" aria-labelledby="spaces-showcase-heading">
            <div className="showcase-header">
                <h2 id="spaces-showcase-heading">Organize Your Work</h2>
                <p>Give each project a Space with its own instructions, files and chats, and Buddy keeps track of where you left off.</p>
            </div>

            <div className="spaces-demo">
                <div className="spaces-demo-rail">
                    <p className="spaces-demo-rail-label">Spaces</p>
                    <div role="tablist" aria-label="Example Spaces" aria-orientation="vertical" className="spaces-demo-tabs">
                        {DEMO_SPACES.map((s, i) => {
                            const selected = s.id === activeId;
                            return (
                                <button
                                    key={s.id}
                                    ref={el => { tabRefs.current[s.id] = el; }}
                                    type="button"
                                    role="tab"
                                    id={`space-tab-${s.id}`}
                                    aria-selected={selected}
                                    aria-controls="space-demo-panel"
                                    tabIndex={selected ? 0 : -1}
                                    className={`spaces-demo-tab ${selected ? 'is-selected' : ''}`}
                                    onClick={() => select(s.id)}
                                    onKeyDown={e => onTabKeyDown(e, i)}
                                >
                                    <Folder01Icon size={18} aria-hidden />
                                    <span className="spaces-demo-tab-text">
                                        <span className="spaces-demo-tab-title">{s.title}</span>
                                        <span className="spaces-demo-tab-meta">
                                            {s.chats.length} Chats · {s.files.length} Files
                                        </span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div
                    role="tabpanel"
                    id="space-demo-panel"
                    aria-labelledby={`space-tab-${space.id}`}
                    className="spaces-demo-main"
                    key={space.id}
                >
                    <h3 className="spaces-demo-title">{space.title}</h3>

                    <div className="spaces-demo-block">
                        <p className="spaces-demo-label">Instructions</p>
                        <p className="spaces-demo-instructions">{space.instructions}</p>
                    </div>

                    <div className="spaces-demo-columns">
                        <div className="spaces-demo-block">
                            <div className="spaces-demo-label-row">
                                <p className="spaces-demo-label">Materials</p>
                                <span className="spaces-demo-capacity-value">{space.capacity}%</span>
                            </div>
                            <div
                                className="spaces-demo-capacity"
                                role="progressbar"
                                aria-label="Knowledge capacity used"
                                aria-valuenow={space.capacity}
                                aria-valuemin={0}
                                aria-valuemax={100}
                            >
                                <span style={{ width: `${space.capacity}%` }} />
                            </div>
                            <ul className="spaces-demo-list">
                                {space.files.map(f => (
                                    <li key={f.name}>
                                        <span className={`spaces-demo-file-icon is-${f.kind}`} aria-hidden>{FILE_ICON[f.kind]}</span>
                                        <span className="spaces-demo-item-title">{f.name}</span>
                                        <span className="spaces-demo-item-meta">{f.size}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="spaces-demo-block">
                            <p className="spaces-demo-label">Conversations</p>
                            <ul className="spaces-demo-list">
                                {space.chats.map(c => (
                                    <li key={c.title}>
                                        <span className="spaces-demo-file-icon" aria-hidden><Message01Icon size={18} /></span>
                                        <span className="spaces-demo-item-title">{c.title}</span>
                                        <span className="spaces-demo-item-meta">{c.when}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="spaces-demo-recap" aria-label="Example of a new chat in this Space">
                        <p className="spaces-demo-bubble is-user">{space.recap.question}</p>
                        <p className="spaces-demo-bubble is-buddy">{space.recap.answer}</p>
                    </div>
                </div>
            </div>

            <FeatureCards items={FEATURES} label="What Spaces do" />

            {!isExternal && (
                <div className="showcase-cta study-spaces-cta">
                    <div className="cta-content">
                        <h3>Spaces Come With Ultimate</h3>
                        <p>Try them free for {TRIAL_DAYS} days with the Ultimate trial.</p>
                        <button type="button" className="pricing-button is-primary study-spaces-cta-button" onClick={scrollToPricing}>
                            See Plans
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
};

export default SpaceShowcase;
